import { useState } from 'react';

import localServerApi from '@/api/localServer';

import Button from '@/components/buttons-links/Button';
import TextLink from '@/components/buttons-links/TextLink';
import FormGroup from '@/components/forms/FormGroup';
import FormText from '@/components/forms/FormText';

import { useAnaliticsContext } from '@/context/Analytics';
import { useNotificationContext } from '@/context/Notification';
import { useUserContext } from '@/context/User';

import useEvent from '@/hooks/useEvent';
import useMount from '@/hooks/useMount';
import useStateMergeUpdater from '@/hooks/useStateMergeUpdater';

import route from '@/lib/routes';
import { isValidEmail } from '@/lib/validation';

import * as S from './index.styles';

export default function LoginForm({
  className,
  onLogin = () => null,
  onLoginError = () => null,
}) {
  const { authenticating, login } = useUserContext();
  const { create: createNotification } = useNotificationContext();
  const { trackUserLoginLinkedInConversion } = useAnaliticsContext();

  // TODO can receive email as query param
  const [formState, setFormState] = useState(() => ({
    email: '',
    password: '',
    csrfToken: null,
    emailValid: false,
    passwordValid: false,
    dirty: false,
  }));

  const { email, password, emailValid, csrfToken, passwordValid, dirty } =
    formState;

  const updateFormState = useStateMergeUpdater(setFormState);

  const onEmailChange = useEvent((evt) => {
    // Make sure email is always lowercase
    const email = evt.target.value.toLowerCase().replace(/\s/g, '');

    updateFormState({ email, emailValid: !!email && isValidEmail(email) });
  });

  const onPasswordChange = useEvent((evt) => {
    const { value } = evt.target;

    updateFormState({
      password: value,
      passwordValid: Boolean(value),
    });
  });

  const isFormValid = useEvent(
    () => !!email && !!password && isValidEmail(email),
  );

  const onSubmit = useEvent(async (evt) => {
    evt.preventDefault();

    updateFormState({ dirty: true });

    if (!isFormValid()) {
      return;
    }

    try {
      await login(email, password, csrfToken);
      trackUserLoginLinkedInConversion();
      onLogin();
    } catch (err) {
      createNotification(err.displayError, 'warning');
      onLoginError(err);
    }
  });

  useMount(() => {
    (async () => {
      try {
        const { result } = await localServerApi.auth.retrieveDPCSRFToken();
        updateFormState({ csrfToken: result });
      } catch (err) {
        console.error('Unable to load CSRF token.', err);
      }
    })();
  });

  return (
    <S.Form onSubmit={onSubmit} className={className}>
      <FormGroup
        name="email"
        label="Email"
        errorMessage={dirty && !emailValid ? 'Please enter a valid email.' : ''}
      >
        <FormText
          name="email"
          placeholder="Enter your email address"
          value={email}
          onChange={onEmailChange}
        />
      </FormGroup>

      <FormGroup
        name="password"
        label="Password"
        errorMessage={
          dirty && !passwordValid ? 'Please enter a valid password.' : ''
        }
      >
        <FormText
          name="password"
          placeholder="Enter your password"
          type="password"
          value={password}
          onChange={onPasswordChange}
        />
      </FormGroup>

      <Button
        type="submit"
        widthSize="full"
        processing={authenticating}
        disabled={authenticating}
      >
        Log In
      </Button>

      <TextLink size="small" to={route('forgotPassword')}>
        Forgot password
      </TextLink>
    </S.Form>
  );
}
